import { VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import { LocalShift, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import { visitResourceStatus } from "~/utils/statuses";
import type { LocalVisitResourceWithOptionalFriction } from "./driveTimeOptimizerConversion";
import { validateProviderShift } from "./visitValidationUtils";

type FrictionOverride = Pick<LocalVisitResourceWithOptionalFriction, "startTimeFriction" | "assignmentFriction">;

//----------------------------------------
// Limited scope drive time optimizer setup
//----------------------------------------

// Single-provider optimize: that provider's visits can move freely, siblings on
// those visits move with medium start-time friction, and everything else stays
// at the caller's frozen default.
export const applyProviderOptimizeFriction = (
  localVisitResources: LocalVisitResource[],
  providerId: number,
): LocalVisitResourceWithOptionalFriction[] => {
  const targetResources = localVisitResources.filter((resource) =>
    resource.providerId === providerId
    && visitResourceStatus(resource) !== VisitEventStatusEnum.Canceled,
  );

  return applyTargetAndSiblingFriction(
    localVisitResources,
    targetResources,
    { startTimeFriction: "none" },
    { startTimeFriction: "medium" },
  );
};

// Assign-missing-providers optimize: unassigned resources and resources that
// fall outside a confirmed shift can move freely. Other resources on those
// visits keep high start-time and assignment friction.
export const applyAssignMissingProvidersFriction = (
  localVisitResources: LocalVisitResource[],
  localShifts: LocalShift[],
): LocalVisitResourceWithOptionalFriction[] => {
  const targetResources = localVisitResources.filter((resource) =>
    resourceNeedsProviderAssignment(resource, localShifts),
  );

  return applyTargetAndSiblingFriction(
    localVisitResources,
    targetResources,
    { startTimeFriction: "none", assignmentFriction: "none" },
    { startTimeFriction: "high", assignmentFriction: "high" },
  );
};

//----------------------------------------
// Helper functions
//----------------------------------------

// An active, scheduled resource needs a provider when it has none, or when its
// provider has no confirmed shift covering the visit. This is the same shift
// check that paints a command-center visit red for "no shift" or "outside shift
// hours"; drive-time buffer warnings are a separate check and are ignored here.
export const resourceNeedsProviderAssignment = (
  resource: LocalVisitResource,
  localShifts: LocalShift[],
): boolean => {
  if (visitResourceStatus(resource) === VisitEventStatusEnum.Canceled || !resource.startTime) return false;
  if (resource.providerId == null) return true;
  if (!resource.endTime) return false;

  return validateProviderShift(
    resource.providerId,
    localShifts,
    new Date(resource.startTime),
    new Date(resource.endTime),
  ).length > 0;
};

// Applies targetFriction to the given resources, siblingFriction to the other
// resources on those visits, and leaves everything else untouched.
const applyTargetAndSiblingFriction = (
  localVisitResources: LocalVisitResource[],
  targetResources: LocalVisitResource[],
  targetFriction: FrictionOverride,
  siblingFriction: FrictionOverride,
): LocalVisitResourceWithOptionalFriction[] => {
  const targetResourceIds = new Set(targetResources.map((resource) => resource.id));
  const targetVisitIds = new Set(targetResources.map((resource) => resource.visitId));

  return localVisitResources.map((resource) => {
    if (targetResourceIds.has(resource.id)) return { ...resource, ...targetFriction };
    if (targetVisitIds.has(resource.visitId)) return { ...resource, ...siblingFriction };
    return resource;
  });
};
